import React from 'react';
import '../../App.scss';
import { PACKAGING_TYPES, UNIT_TYPES, WAREHOUSE_STOCK_TYPES, resolveEnum } from '../../Constants';
import { Form, Button, Table, Modal } from 'react-bootstrap';
import { PlusSquare } from 'react-bootstrap-icons';
import AuthorizedPage from '../../component/AuthorizedPage';
import ProductItemApi from '../../api/ProductItemApi';
import { updateState } from '../StateHelper';
import { Role } from '../../domain/Role';

interface ProductPoolItem {
  id?: number;
  name?: string;
  stockCode?: string;
  barcode?: string;
  notes?: string;
  packagingType?: string;
  packagingUnitType?: string;
  packagingUnitTypeAmount?: string;
  warehouseStockType?: string;
  quantityInsideBox?: number;
  frozen?: boolean;
  width?: string;
  length?: string;
  height?: string;
}

interface State {
  productPoolItemsRaw: ProductPoolItem[];
  productPoolItems: ProductPoolItem[];
  showProductCreateModal: boolean;
  selectedProductPoolItem: ProductPoolItem;
  syncLoading: boolean;
  showProductDetailModal: boolean;
  page: number;
  totalPages: number;
  search: string;
  pageSize: number;
  showPassive: boolean;
  syncRunning: boolean;
  syncProcessed: number;
  syncFailed: number;
  syncDone: boolean;
}

const initialState: State = {
  productPoolItemsRaw: [],
  productPoolItems: [],
  showProductCreateModal: false,
  selectedProductPoolItem: {},
  syncLoading: false,
  showProductDetailModal: false,
  page: 0,
  totalPages: 0,
  search: "",
  pageSize: 50,
  showPassive: false,
  syncRunning: false,
  syncProcessed: 0,
  syncFailed: 0,
  syncDone: false
};

const PAGE_SIZE_OPTIONS = [20, 50, 100, 200];

class ProductItem extends React.Component<any, State> {
  searchTimer: any;
  syncPoller: any;

  constructor(props: any) {
    super(props);
    this.onSubmit = this.onSubmit.bind(this);
    this.state = initialState;
  }

  componentDidMount() {
    this.loadPoolItems();
    // Sayfa acilirken sync zaten calisiyorsa durumu yakala ve takibe basla
    this.checkSyncStatusOnce();
  }

  componentWillUnmount() {
    clearInterval(this.syncPoller);
    clearTimeout(this.searchTimer);
  }

  checkSyncStatusOnce = async () => {
    const res = await ProductItemApi.synchronizeStatus();
    const d = res?.data;
    if (d?.running) {
      this.setState({ syncLoading: true, syncRunning: true, syncProcessed: d.processed || 0, syncFailed: d.failed || 0 });
      this.startSyncPolling();
    }
  };

  startSyncPolling = () => {
    clearInterval(this.syncPoller);
    this.syncPoller = setInterval(async () => {
      const res = await ProductItemApi.synchronizeStatus();
      const d = res?.data;
      if (!d) return;
      this.setState({ syncRunning: d.running, syncProcessed: d.processed || 0, syncFailed: d.failed || 0 });
      if (!d.running) {
        clearInterval(this.syncPoller);
        this.setState({ syncLoading: false, syncDone: true });
      }
    }, 2500);
  };

  // Server-side: arama + sayfa backend'e gider, filtreli + sayfalı döner
  async loadPoolItems(page: number = this.state.page, search: string = this.state.search, size: number = this.state.pageSize) {
    const response = this.state.showPassive
      ? await ProductItemApi.listPagedPassive(search, page, size)
      : await ProductItemApi.listPaged(search, page, size);
    if (response.success && response.data) {
      this.setState({
        productPoolItems: response.data.content || [],
        totalPages: response.data.totalPages || 0,
        page: response.data.number || 0,
      });
    }
  }

  onSearchChange = (value: string) => {
    this.setState({ search: value });
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.loadPoolItems(0, value), 350);
  };

  onPageSizeChange = (size: number) => {
    this.setState({ pageSize: size });
    this.loadPoolItems(0, this.state.search, size);
  };

  togglePassive = () => {
    // Aktif <-> pasif liste arasinda gecis; arama ve sayfa sifirlanir
    this.setState({ showPassive: !this.state.showPassive, page: 0 }, () => {
      this.loadPoolItems(0, this.state.search, this.state.pageSize);
    });
  };

  async reactivateItem(id: number) {
    if (!window.confirm("Bu ürünü tekrar aktif etmek istediğinize emin misiniz?")) return;
    const response = await ProductItemApi.reactivate(id);
    if (response.success) {
      this.loadPoolItems();
    }
  }

  renderPagination = () => {
    const { page, totalPages, pageSize } = this.state;
    return (
      <div className="d-flex justify-content-center align-items-center mt-3">
        <Button size="sm" variant="outline-secondary" className="me-3" disabled={page <= 0} onClick={() => this.loadPoolItems(page - 1)}>← Önceki</Button>
        <span>Sayfa {totalPages === 0 ? 0 : page + 1} / {totalPages}</span>
        <Button size="sm" variant="outline-secondary" className="ms-3" disabled={page >= totalPages - 1} onClick={() => this.loadPoolItems(page + 1)}>Sonraki →</Button>
        <Form.Select
          size="sm"
          className="ms-4"
          style={{ width: "auto" }}
          value={pageSize}
          onChange={(e) => this.onPageSizeChange(Number(e.target.value))}
        >
          {PAGE_SIZE_OPTIONS.map((n) => (
            <option key={n} value={n}>{n} / sayfa</option>
          ))}
        </Form.Select>
      </div>
    );
  };

  async onSubmit(e: any) {
    e.preventDefault();
    const formData = new FormData(e.target);
    const formObject: any = Object.fromEntries(formData);
    // Düzenlemede id'yi state'ten kesin bağla → yeni kayıt yerine güncelle (PUT)
    const editingId = this.state.selectedProductPoolItem?.id;
    if (editingId) formObject.id = editingId;
    const response = await ProductItemApi.createOrUpdateProductItemPool(formObject);
    if (response.success) {
      e.target.reset();
      updateState(this, "showProductCreateModal", false);
      updateState(this, "selectedProductPoolItem", {});
      this.loadPoolItems();
    }
  }

  onModalClose() {
    updateState(this, "showProductCreateModal", false);
    updateState(this, "selectedProductPoolItem", {});
    this.loadPoolItems();
  }

  updateProducPoolItemInfo(selected: ProductPoolItem) {
    updateState(this, "selectedProductPoolItem", selected);
    updateState(this, "showProductCreateModal", true);
  }

  productPoolItemTable = () => (
    <Table striped bordered hover>
      <thead>
        <tr>
          <th>Stok Kodu</th>
          <th>Barcode</th>
          <th>Ürün Adı</th>
          <th>Stoklama Şekli</th>
          <th>İşlem</th>
        </tr>
      </thead>
      <tbody>
        {this.productPoolItemTableRows(this.state.productPoolItems)}
      </tbody>
    </Table>
  );

  productPoolItemTableRows = (items: any[]) =>
    items?.map((it) => (
      <tr key={it.id}>
        <td>{it.stockCode || ""}</td>
        <td>{it.barcode || ""}</td>
        <td>{it.name || ""}</td>
        <td>{it.packagingUnitTypeAmount || ""} {resolveEnum(it.packagingUnitType)} {resolveEnum(it.packagingType)}</td>
        <td>
          <Button
            size="sm"
            variant="secondary"
            className="me-2"
            onClick={() => this.updateProducPoolItemInfo(it)}
          >
            Düzenle
          </Button>
          <Button
            size="sm"
            variant="info"
            onClick={() => {
              updateState(this, "selectedProductPoolItem", it);
              updateState(this, "showProductDetailModal", true);
            }}
          >
            Detaylı İncele
          </Button>
          {this.state.showPassive ? (
            <Button
              size="sm"
              variant="outline-success"
              className="ms-2"
              onClick={() => this.reactivateItem(it.id)}
            >
              Geri Al
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline-danger"
              className="ms-2"
              onClick={async () => {
                if (!window.confirm(`"${it.name}" pasife alınsın mı? (Listeden kalkar)`)) return;
                const res = await ProductItemApi.deactivate(it.id);
                if (res?.success) this.loadPoolItems();
                else alert("Pasife alınamadı.");
              }}
            >
              Pasife Al
            </Button>
          )}
        </td>
      </tr>
    )) || null;

  renderDropdownOptions = (items: { key: string; value: string }[] = []) => {
    return items
      .filter(it => it != null)
      .map(it => <option key={it.key} value={it.key}>{it.value}</option>);
  };

  renderProductDetailModal = () => {
    const selected = this.state.selectedProductPoolItem;
    if (!selected) return null;

    return (
      <Modal
        show={this.state.showProductDetailModal}
        onHide={() => updateState(this, "showProductDetailModal", false)}
        dialogClassName="modal-90w"
      >
        <Modal.Header closeButton>
          <Modal.Title>Ürün Detayı</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="centered-80-percent margin-left-right-40">
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Ürün Adı</Form.Label>
                <Form.Control type="text" value={selected.name || ""} disabled />
              </Form.Group>

              <div className="row mb-3">
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Stok Kodu</Form.Label>
                    <Form.Control type="text" value={selected.stockCode || ""} disabled />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Barcode</Form.Label>
                    <Form.Control type="text" value={selected.barcode || ""} disabled />
                  </Form.Group>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Paketleme Tipi</Form.Label>
                    <Form.Control type="text" value={resolveEnum(selected.packagingType) || ""} disabled />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Depo Stok Tipi</Form.Label>
                    <Form.Control type="text" value={resolveEnum(selected.warehouseStockType) || ""} disabled />
                  </Form.Group>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Paketleme Ölçü Birimi</Form.Label>
                    <Form.Control type="text" value={resolveEnum(selected.packagingUnitType) || ""} disabled />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Paket Ölçü Miktarı</Form.Label>
                    <Form.Control type="text" value={selected.packagingUnitTypeAmount || ""} disabled />
                  </Form.Group>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-4">
                  <Form.Group>
                    <Form.Label>Genişlik (cm)</Form.Label>
                    <Form.Control type="text" value={selected.width || ""} disabled />
                  </Form.Group>
                </div>
                <div className="col-md-4">
                  <Form.Group>
                    <Form.Label>Uzunluk (cm)</Form.Label>
                    <Form.Control type="text" value={selected.length || ""} disabled />
                  </Form.Group>
                </div>
                <div className="col-md-4">
                  <Form.Group>
                    <Form.Label>Yükseklik (cm)</Form.Label>
                    <Form.Control type="text" value={selected.height || ""} disabled />
                  </Form.Group>
                </div>
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Koli İçi Adet</Form.Label>
                <Form.Control type="text" value={selected.quantityInsideBox || ""} disabled />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Notlar</Form.Label>
                <Form.Control as="textarea" rows={5} value={selected.notes || ""} disabled />
              </Form.Group>
            </Form>
          </div>
        </Modal.Body>
      </Modal>
    );
  };

  renderProductCreateModal = () => {
    const selected = this.state.selectedProductPoolItem;

    const packagingTypeOptions = this.renderDropdownOptions(PACKAGING_TYPES);
    const packagingUnitTypeOptions = this.renderDropdownOptions(UNIT_TYPES);
    const warehouseStockTypeOptions = this.renderDropdownOptions(WAREHOUSE_STOCK_TYPES);

    return (
      <Modal
        autoFocus
        show={this.state.showProductCreateModal}
        onHide={() => this.onModalClose()}
        dialogClassName="modal-90w"
      >
        <Modal.Header closeButton>
          <Modal.Title>Stoklu Ürün Ekle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="centered-80-percent margin-left-right-40">
            <Form key={selected.id || "new"} onSubmit={this.onSubmit}>
              <Form.Control type="hidden" name="id" defaultValue={selected.id || ""} />

              <Form.Group className="mb-3">
                <Form.Label>Ürün Adı</Form.Label>
                <Form.Control
                  name="name"
                  type="text"
                  placeholder="Ürün Adı"
                  defaultValue={selected.name || ""}
                  required
                />
              </Form.Group>

              <div className="row mb-3">
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Stok Kodu</Form.Label>
                    <Form.Control
                      name="stockCode"
                      type="text"
                      placeholder="Stok Kodu"
                      defaultValue={selected.stockCode || ""}
                      required
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Barcode</Form.Label>
                    <Form.Control
                      name="barcode"
                      type="text"
                      placeholder="Barcode"
                      defaultValue={selected.barcode || ""}
                      required
                    />
                  </Form.Group>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Paketleme Tipi</Form.Label>
                    <Form.Select
                      name="packagingType"
                      defaultValue={selected.packagingType || ""}

                      required
                    >
                      <option value="">Seçiniz</option>
                      {packagingTypeOptions}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Depo Stok Tipi</Form.Label>
                    <Form.Select
                      name="warehouseStockType"
                      defaultValue={selected.warehouseStockType || ""}
                      disabled={!!selected.id}
                      required
                    >
                      <option value="">Seçiniz</option>
                      {warehouseStockTypeOptions}
                    </Form.Select>
                  </Form.Group>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Paketleme Ölçü Birimi</Form.Label>
                    <Form.Select
                      name="packagingUnitType"
                      defaultValue={selected.packagingUnitType || ""}
                      required
                    >
                      <option value="">Seçiniz</option>
                      {packagingUnitTypeOptions}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Paket Ölçü Miktarı</Form.Label>
                    <Form.Control
                      name="packagingUnitTypeAmount"
                      type="text"
                      placeholder="Miktar"
                      defaultValue={selected.packagingUnitTypeAmount || ""}
                      required
                    />
                  </Form.Group>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-4">
                  <Form.Group>
                    <Form.Label>Genişlik (cm)</Form.Label>
                    <Form.Control
                      name="width"
                      type="text"
                      placeholder="Genişlik"
                      defaultValue={selected.width || ""}
                      required
                    />
                  </Form.Group>
                </div>
                <div className="col-md-4">
                  <Form.Group>
                    <Form.Label>Uzunluk (cm)</Form.Label>
                    <Form.Control
                      name="length"
                      type="text"
                      placeholder="Uzunluk"
                      defaultValue={selected.length || ""}
                      required
                    />
                  </Form.Group>
                </div>
                <div className="col-md-4">
                  <Form.Group>
                    <Form.Label>Yükseklik (cm)</Form.Label>
                    <Form.Control
                      name="height"
                      type="text"
                      placeholder="Yükseklik"
                      defaultValue={selected.height || ""}
                      required
                    />
                  </Form.Group>
                </div>
              </div>

              {/* Koli İçi Adet */}
              <Form.Group className="mb-3">
                <Form.Label>Koli İçi Adet</Form.Label>
                <Form.Control
                  name="quantityInsideBox"
                  type="number"
                  placeholder="Koli içi adet"
                  defaultValue={selected.quantityInsideBox || 1}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Notlar</Form.Label>
                <Form.Control
                  as="textarea"
                  name="notes"
                  rows={5}
                  placeholder="Notlarınızı buraya yazınız..."
                  defaultValue={selected.notes || ""}
                />
              </Form.Group>

              <Button style={{ float: "right" }} variant="secondary" type="submit">
                {selected.id ? "Ürün Bilgilerini Güncelle" : "Ürün Ekle"}
              </Button>
            </Form>
          </div>
        </Modal.Body>
      </Modal>
    );
  };

  render() {
    return (
      <AuthorizedPage role={Role.SALES_ADMIN}>
        {this.renderProductCreateModal()}
        {this.renderProductDetailModal()}
        <div className="centered-95-percent">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h3 className="mb-0">📦 Depolanabilir Ürün Listesi{this.state.showPassive ? " — Pasifler" : ""}</h3>
            <div>
              <Button
                variant={this.state.showPassive ? "secondary" : "outline-secondary"}
                className="me-2"
                onClick={this.togglePassive}
              >
                {this.state.showPassive ? "← Aktif Ürünlere Dön" : "Pasif Ürünleri Listele"}
              </Button>
              <Button
                variant="warning"
                className="me-2"
                disabled={this.state.syncLoading}
                onClick={async () => {
                  try {
                    this.setState({ syncLoading: true, syncDone: false, syncRunning: true, syncProcessed: 0, syncFailed: 0 });
                    // Backend artik async: aninda doner, sync arka planda calisir -> durumu polling ile takip et
                    await ProductItemApi.synchronizeProductItems();
                    this.startSyncPolling();
                  } catch (error) {
                    console.error(error);
                    this.setState({ syncLoading: false, syncRunning: false });
                    alert("Senkronizasyon başlatılamadı ❌");
                  }
                }}
              >
                {this.state.syncLoading ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> : "🔄 Senkronize Et"}
              </Button>
              {(this.state.syncRunning || this.state.syncDone) && (
                <span className="ms-2" style={{ fontSize: "0.9rem" }}>
                  {this.state.syncRunning
                    ? `⏳ Senkronize ediliyor… İşlenen: ${this.state.syncProcessed}, Atlanan: ${this.state.syncFailed}`
                    : `✓ Bitti. İşlenen: ${this.state.syncProcessed}, Atlanan: ${this.state.syncFailed} — onay sayfasında listelenir.`}
                </span>
              )}
              <Button
                variant="success"
                onClick={() => this.setState({ showProductCreateModal: true, selectedProductPoolItem: {} })}
              >
                <PlusSquare size={18} /> Ürün Ekle
              </Button>
            </div>
          </div>

          <div className="mb-2">
            <div className="search-container">
              <input
                type="text"
                placeholder="Ara"
                value={this.state.search}
                onChange={(e) => this.onSearchChange(e.target.value)}
                className="search-input"
              />
              <div className="search-icon"></div>
            </div>
          </div>

          {this.productPoolItemTable()}
          {this.renderPagination()}
        </div>
      </AuthorizedPage>
    );
  }
}

export default ProductItem;
